import {
  createStyles,
  Grid,
  makeStyles,
  Theme,
  Typography,
} from '@material-ui/core';
import React, { useRef, useState } from 'react';
import { useTranslation } from '../../../hooks/useTranslation';
import DatePicker from '../../date-picker';
import IdentityVerification from './IdentityVerification';
import moment from 'moment-timezone';
import { BaseFormModel } from '../../../hooks/useFormState.types';
import {
  PersonalIdentificationType,
  StateIdType,
} from '../../../__generated__/pge-types';
import VerifyExistingCustomerIdentity from './IdentityVerification/VerifyExistingCustomerIdentity';
import EmancipatedMinorTooltip from './EmancipatedMinorTooltip';
import colors from '../../../themes/main-colors';

const CALENDAR_INITIAL_DATE_SELECTION = '01/01/1990';
const DATE_FORMAT = 'MM/dd/yyyy';

interface Props {
  form: BaseFormModel;
  primaryIdType?: PersonalIdentificationType;
  isCoCustomer?: boolean;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    emancipatedMinorText: {
      color: colors.sparkBlue,
      borderBottom: `1px dotted ${colors.sparkBlue}`,
      cursor: 'pointer',
    },
    verification: {
      marginTop: '24px',
    },
  }),
);

export const SSMCustomerVerification = ({
  form,
  primaryIdType,
  isCoCustomer = false,
}: Props) => {
  const { t, richT } = useTranslation();
  const anchorRef = useRef<any>();
  const classes = useStyles();
  const [tooltipAnchor, setTooltipAnchor] = useState<HTMLElement | null>(null);

  const initialDateSelection = moment(CALENDAR_INITIAL_DATE_SELECTION).toDate();

  const openTooltip = () => setTooltipAnchor(anchorRef.current);

  const closeTooltip = () => setTooltipAnchor(null);

  return (
    <Grid
      container
      direction="column"
      spacing={4}
      data-testid="cocustomer-verify"
    >
      {primaryIdType &&
      [
        PersonalIdentificationType.Dl,
        PersonalIdentificationType.Ssn,
        PersonalIdentificationType.Ortrib,
        PersonalIdentificationType.Visa,
        PersonalIdentificationType.Matr,
        PersonalIdentificationType.Military,
        PersonalIdentificationType.Resalien,
        PersonalIdentificationType.Itin,
        PersonalIdentificationType.Pssprt,
        PersonalIdentificationType.Duns,
        PersonalIdentificationType.Ein,
        PersonalIdentificationType.Wrksch,
        PersonalIdentificationType.Essid,
        StateIdType.StateIdCard,
        StateIdType.DriverLicense,
      ].includes(primaryIdType) &&
      primaryIdType !== PersonalIdentificationType.None ? (
        <VerifyExistingCustomerIdentity
          form={form}
          primaryIdType={primaryIdType}
          isCoCustomer={isCoCustomer}
        />
      ) : (
        <>
          <IdentityVerification form={form} />

          <Grid item container>
            <DatePicker
              style={{ width: '100%' }}
              label={t('BIRTH_DATE')}
              data-testid="birth-date-picker"
              name="date"
              format={DATE_FORMAT}
              initialFocusedDate={initialDateSelection}
              {...form.props('birthdate')}
              mask={'__/__/____'}
              labelShrink={{ shrink: true }}
            />

            <Typography ref={anchorRef} variant="caption">
              {t('ARE_YOU_AN_EMANCIPATED_MINOR')}{' '}
              <span
                onClick={(e: any) => {
                  e.preventDefault();
                  openTooltip();
                }}
                className={classes.emancipatedMinorText}
              >
                {t('EMANCIPATED_MINOR')}?
              </span>
            </Typography>

            <EmancipatedMinorTooltip
              isOpen={Boolean(tooltipAnchor)}
              anchorEl={tooltipAnchor}
              closeTooltip={closeTooltip}
              content={
                isCoCustomer
                  ? richT('EMANCIPATED_MINOR_CO_CUSTOMER_TOOLTIP_CONTENT')
                  : richT('EMANCIPATED_MINOR_TOOLTIP_CONTENT')
              }
            />
            <Typography className={classes.verification}>
              {t('THIS_VERIFICATION_WILL_NOT_AFFECT_YOU_CREDIT_SCORE')}
            </Typography>
          </Grid>
        </>
      )}
    </Grid>
  );
};

export default SSMCustomerVerification;
